import { useEffect, useMemo, useRef, useState, type ChangeEvent, type ReactElement, type ReactNode } from "react";
import { ArrowRight, Camera, RotateCcw, Save, Trash2, Unlink } from "lucide-react";
import { Badge, Button, FormField, Select, Stack, Surface, Text, TextInput } from "@ux/primitives";
import { BottomActionBar, Toolbar, UserPicker, WorkspacePicker } from "@ux/compositions";
import { useViewport } from "@ux/hooks";
import { palette, space } from "@ux/tokens";
import { useCanEdit } from "@shell/auth/useCanEdit";
import { formatActor } from "../common/formatting";
import { describeApiError } from "../common/errors";
import { useSageOffices } from "../sage-offices/hooks";
import { useUserDirectoryEntry } from "../users/hooks";
import { formatUserDisplay } from "../users/display";
import type { UserDirectoryEntry } from "../users/types";
import { useWorkspaceEntry } from "../workspaces/hooks";
import type { WorkspaceView } from "../workspaces/types";
import { useAssetAssignments } from "../asset-assignments/hooks";
import { ScanBarcodeButton } from "./barcodeScan/ScanBarcodeButton";
import { AssignTargetDialog, type AssignTargetDialogMode } from "./AssignTargetDialog";
import { TargetKindToggle } from "./TargetKindToggle";
import { formatAssetDisplay } from "./display";
import {
    assetTypes,
    type AiFilledFields,
    type AssetAssignTarget,
    type AssetRecognitionSuggestion,
    type AssetType,
    type AssetUpsertPayload,
    type AssetView,
    type ScanFilledFields,
} from "./types";
import { useAssetMatch } from "./hooks";
import { usePhotoFill } from "./photoFill/usePhotoFill";

/**
 * sessionStorage keys for the "remember target for next time" feature on
 * the standalone-create inline picker. Two buckets — workspace and user
 * — so a desk-walking campaign and a laptop-issuing campaign don't
 * clobber each other's sticky pick.
 */
const STICKY_ASSIGN_TO_WORKSPACE_KEY = "office.assets.lastAssignToWorkspaceId";
const STICKY_ASSIGN_TO_USER_KEY = "office.assets.lastAssignToUserId";

/**
 * Asset types that default the inline-assign and Assign/Re-assign dialogs
 * to "User" instead of the broader "Workspace" default. Today laptops
 * and headsets travel with a person; phones may join this set when
 * issued individually instead of pooled. Kept as a single in-module
 * constant so the smart-default rule is one grep away.
 */
const PERSONAL_ASSET_TYPES: ReadonlySet<AssetType> = new Set(["Laptop", "Headset"]);

function smartDefaultKindForType(type: string): AssetAssignTarget["kind"] {
    return PERSONAL_ASSET_TYPES.has(type as AssetType) ? "User" : "Workspace";
}

export type AssetFormMode =
    | { kind: "create" }
    | { kind: "edit"; record: AssetView };

/**
 * Optional context attached when the form is opened from a workspace's
 * "Add new asset" button. When set, the form (a) reads the workspace name
 * into its toolbar so the user knows where their save will land, and
 * (b) when photo-fill detects an existing asset, surfaces a "Re-assign
 * here" CTA via {@link AssetFormProps.onReassignExisting}.
 */
export interface AssetFormWorkspaceContext {
    readonly id: string;
    readonly name: string;
    readonly type: string;
}

export interface AssetFormProps {
    readonly mode: AssetFormMode;
    readonly onSave: (payload: AssetUpsertPayload) => void;
    readonly onCancel: () => void;
    readonly onRequestDelete: (id: string) => void;
    readonly onRequestRestore: (id: string) => void;
    readonly isSaving: boolean;
    readonly errorMessage: string | null;
    /** Set when this form was opened from a workspace's "Add new asset" CTA. */
    readonly workspaceContext?: AssetFormWorkspaceContext;
    /**
     * Fired when the user picks "Re-assign here" on the photo-fill match
     * strip. The parent does the release-then-create dance. Only
     * meaningful when {@link workspaceContext} is also set.
     */
    readonly onReassignExisting?: (existing: AssetView) => void;
    /**
     * Edit-mode only. Fired when the user picks a target in the
     * "Assign to…" dialog on an asset that has no open assignment.
     * Parent should create a new assignment (workspace- or user-target
     * per the discriminator). Optional so non-Asset-page hosts (e.g.
     * inventory mode) that don't surface assignment management can
     * leave the section quiet.
     */
    readonly onAssignNew?: (target: AssetAssignTarget) => void;
    /**
     * Edit-mode only. Fired when the user confirms a Re-assign on an
     * asset that already has an open assignment. Parent should release
     * the current assignment, then create a new one. The current
     * assignment id is provided so the parent doesn't need to refetch.
     */
    readonly onReassign?: (currentAssignmentId: string, target: AssetAssignTarget) => void;
    /**
     * Edit-mode only. Fired when the user picks "Release" on an asset
     * with an open assignment. Parent should release the current
     * assignment and refetch.
     */
    readonly onReleaseAssignment?: (currentAssignmentId: string) => void;
    /** Disables Assign / Re-assign / Release buttons while a related mutation is in-flight. */
    readonly isAssignmentBusy?: boolean;
    /** Surfaced inside the assignment section if the latest assign/reassign/release failed. */
    readonly assignmentErrorMessage?: string | null;
}

interface FormState {
    readonly id: string;
    readonly sageOfficeId: string;
    readonly type: string;
    readonly make: string;
    readonly model: string;
    readonly serialNumber: string;
    readonly assetTag: string;
    /** ISO `"YYYY-MM-DD"` for `<input type="date">`. Empty string = unset. */
    readonly purchaseDate: string;
    /** Raw text from `<input type="number">`. Empty string = unset. */
    readonly purchasePrice: string;
}

function formatAssetIdentity(record: AssetView): string {
    if (record.assetTag !== null && record.assetTag !== undefined && record.assetTag !== "") {
        return `${record.assetTag} · ${record.make}`;
    }
    return `${record.make} ${record.model}`;
}

export function AssetForm({
    mode,
    onSave,
    onCancel,
    onRequestDelete,
    onRequestRestore,
    isSaving,
    errorMessage,
    workspaceContext,
    onReassignExisting,
    onAssignNew,
    onReassign,
    onReleaseAssignment,
    isAssignmentBusy = false,
    assignmentErrorMessage,
}: AssetFormProps): ReactElement {
    const { isPhone } = useViewport();
    const canEdit = useCanEdit();
    const [state, setState] = useState<FormState>(() => initialState(mode));
    const [validationError, setValidationError] = useState<string | null>(null);
    const [aiFilledFields, setAiFilledFields] = useState<AiFilledFields>({});
    const [scanFilledFields, setScanFilledFields] = useState<ScanFilledFields>({});
    const [photoFillError, setPhotoFillError] = useState<string | null>(null);
    // True after photo-fill returned a suggestion; gates the "we already
    // have this" lookup so we don't fire it on every keystroke.
    const [matchProbeArmed, setMatchProbeArmed] = useState(false);
    // User can dismiss the match strip without saving — once dismissed for
    // a given suggestion, don't re-show it until the next photo-fill.
    const [matchDismissed, setMatchDismissed] = useState(false);
    // Inline "Assign to" picker. Per-target buckets so a Workspace pick
    // and a User pick survive flipping the toggle back and forth (and the
    // sticky-storage round-trip). Only meaningful when the parent has not
    // already pinned this form to a workspace context.
    const [inlineAssignWorkspace, setInlineAssignWorkspace] = useState<WorkspaceView | null>(null);
    const [inlineAssignUser, setInlineAssignUser] = useState<UserDirectoryEntry | null>(null);
    const [rememberAssignTo, setRememberAssignTo] = useState(true);
    // The kind toggle the user is looking at. If the user has not
    // overridden, this tracks the smart default derived from the asset
    // type. Once the user clicks the toggle, their choice sticks for the
    // remainder of the form session.
    const [userOverrodeKind, setUserOverrodeKind] = useState(false);
    const [overrideKind, setOverrideKind] = useState<AssetAssignTarget["kind"]>("Workspace");
    const fileInputRef = useRef<HTMLInputElement | null>(null);

    const showInlineAssignTo = mode.kind === "create" && workspaceContext === undefined;
    const inlineAssignKind: AssetAssignTarget["kind"] = userOverrodeKind
        ? overrideKind
        : smartDefaultKindForType(state.type);
    // Hydrate the "last used" target from sessionStorage exactly once per
    // create flow, scoped to the current kind. We resolve ids to full
    // entries via the existing entry hooks so the picker's selected card
    // has the friendly name without an extra fetch on the picker side.
    const stickyAssignToWorkspaceId = (() => {
        if (!showInlineAssignTo || inlineAssignKind !== "Workspace") return null;
        try {
            return sessionStorage.getItem(STICKY_ASSIGN_TO_WORKSPACE_KEY);
        } catch {
            return null;
        }
    })();
    const stickyAssignToUserId = (() => {
        if (!showInlineAssignTo || inlineAssignKind !== "User") return null;
        try {
            return sessionStorage.getItem(STICKY_ASSIGN_TO_USER_KEY);
        } catch {
            return null;
        }
    })();
    const stickyAssignToWorkspaceQuery = useWorkspaceEntry(
        inlineAssignWorkspace === null ? stickyAssignToWorkspaceId : null,
    );
    const stickyAssignToUserQuery = useUserDirectoryEntry(
        inlineAssignUser === null ? stickyAssignToUserId : null,
    );

    useEffect(() => {
        if (!showInlineAssignTo) return;
        if (inlineAssignKind !== "Workspace") return;
        if (inlineAssignWorkspace !== null) return;
        const fetched = stickyAssignToWorkspaceQuery.data ?? null;
        if (fetched !== null && !fetched.isDeleted) {
            setInlineAssignWorkspace(fetched);
        }
    }, [showInlineAssignTo, inlineAssignKind, inlineAssignWorkspace, stickyAssignToWorkspaceQuery.data]);

    useEffect(() => {
        if (!showInlineAssignTo) return;
        if (inlineAssignKind !== "User") return;
        if (inlineAssignUser !== null) return;
        const fetched = stickyAssignToUserQuery.data ?? null;
        if (fetched !== null) {
            setInlineAssignUser(fetched);
        }
    }, [showInlineAssignTo, inlineAssignKind, inlineAssignUser, stickyAssignToUserQuery.data]);

    // Mirror the latest `state` into a ref so the photo-fill success
    // callback can read fresh values when it lands. Without this, the
    // mutation's closure captures `state` from the click moment and
    // would clobber anything the user typed during the network wait.
    const stateRef = useRef(state);
    stateRef.current = state;

    const photoFillMutation = usePhotoFill();

    useEffect(() => {
        setState(initialState(mode));
        setValidationError(null);
        setAiFilledFields({});
        setScanFilledFields({});
        setPhotoFillError(null);
        setMatchProbeArmed(false);
        setMatchDismissed(false);
        // Reset target-aware inline picker state on every mode change.
        // Smart default re-derives from type via inlineAssignKind, so we
        // only need to forget the user override here.
        setInlineAssignWorkspace(null);
        setInlineAssignUser(null);
        setUserOverrodeKind(false);
    }, [mode]);

    // Existing-asset detection: on `create` only (we'd never want this on
    // edit — by definition the asset already exists). The probe activates
    // after photo-fill returns a suggestion containing a serial or tag,
    // and de-activates if the user clears those fields. Trimmed values
    // keep the cache key stable across whitespace edits.
    const trimmedSn = state.serialNumber.trim();
    const trimmedTag = state.assetTag.trim();
    const matchProbeActive =
        mode.kind === "create" &&
        matchProbeArmed &&
        !matchDismissed &&
        (trimmedSn !== "" || trimmedTag !== "");
    const { match: existingMatch, isLoading: isProbingMatch } = useAssetMatch({
        serialNumber: matchProbeActive ? trimmedSn : null,
        assetTag: matchProbeActive ? trimmedTag : null,
    });

    function updateField<K extends keyof FormState>(field: K, value: FormState[K]): void {
        setState((p) => ({ ...p, [field]: value }));
        // Manual edits invalidate AI/scan provenance for that field — the
        // user has now taken ownership, so the badge should disappear.
        setAiFilledFields((prev) => {
            const key = field as keyof AiFilledFields;
            if (prev[key] === undefined) return prev;
            return Object.fromEntries(
                Object.entries(prev).filter(([k]) => k !== key),
            ) as AiFilledFields;
        });
        setScanFilledFields((prev) => {
            const key = field as keyof ScanFilledFields;
            if (prev[key] === undefined) return prev;
            return Object.fromEntries(
                Object.entries(prev).filter(([k]) => k !== key),
            ) as ScanFilledFields;
        });
    }

    function applySuggestion(suggestion: AssetRecognitionSuggestion): void {
        // Read the latest committed form state from the ref, not the
        // closure — the user may have typed during the network wait.
        const current = stateRef.current;
        type FillableField = "type" | "make" | "model" | "serialNumber" | "assetTag";
        const updates: Partial<Record<FillableField, string>> = {};
        const nextFilled: AiFilledFields = {};

        const apply = (field: FillableField, raw: string | null): void => {
            if (raw === null || raw === "") return;
            // Don't clobber values the user has already typed.
            if (current[field] !== "") return;
            updates[field] = raw;
            nextFilled[field] = suggestion.confidence[field];
        };

        apply("type", suggestion.type);
        apply("make", suggestion.make);
        apply("model", suggestion.model);
        apply("serialNumber", suggestion.serialNumber);
        apply("assetTag", suggestion.assetTag);

        setState({ ...current, ...updates });
        setAiFilledFields(nextFilled);
        setPhotoFillError(null);
    }

    /**
     * Field-fill from a barcode scan. Mirrors the relevant slice of
     * applySuggestion: stamp the value, replace AI provenance with scan
     * provenance for that field, clear any prior validation error, and
     * re-arm the existing-asset match probe so a scanned tag/serial
     * triggers the same "Looks familiar / Re-assign here" strip the
     * photo-fill flow does. Re-arming is what makes the barcode scanner
     * compose for free with Layer 1.5.
     */
    function applyScanResult(
        field: keyof FormState,
        value: string,
        format: string,
    ): void {
        setState((p) => ({ ...p, [field]: value }));
        setAiFilledFields((prev) => {
            const key = field as keyof AiFilledFields;
            if (prev[key] === undefined) return prev;
            return Object.fromEntries(
                Object.entries(prev).filter(([k]) => k !== key),
            ) as AiFilledFields;
        });
        setScanFilledFields((prev) => ({
            ...prev,
            [field as keyof ScanFilledFields]: format,
        }));
        setValidationError(null);
        if (field === "assetTag" || field === "serialNumber") {
            setMatchProbeArmed(true);
            setMatchDismissed(false);
        }
    }

    function handlePhotoSelected(event: ChangeEvent<HTMLInputElement>): void {
        const file = event.target.files?.[0];
        // Reset the input value so picking the same file again still fires `change`.
        event.target.value = "";
        if (file === undefined) return;

        setPhotoFillError(null);
        // A new photo means a new chance to detect an existing asset, so
        // re-arm the probe and forget any prior dismissal.
        setMatchProbeArmed(false);
        setMatchDismissed(false);
        photoFillMutation.mutate(file, {
            onSuccess: (response) => {
                applySuggestion(response.suggestion);
                setMatchProbeArmed(true);
            },
            onError: (err) => setPhotoFillError(describeApiError(err)),
        });
    }

    function handleSubmit(): void {
        const trimmedTag = state.assetTag.trim();
        const inlineTarget: AssetAssignTarget | null =
            showInlineAssignTo && inlineAssignKind === "Workspace" && inlineAssignWorkspace !== null
                ? { kind: "Workspace", id: inlineAssignWorkspace.id }
                : showInlineAssignTo && inlineAssignKind === "User" && inlineAssignUser !== null
                  ? { kind: "User", id: inlineAssignUser.id }
                  : null;
        const trimmedDate = state.purchaseDate.trim();
        const trimmedPrice = state.purchasePrice.trim();
        // `<input type="number">` returns a string; coerce to number at
        // the submit boundary so the wire payload carries a JSON number
        // and the backend's `decimal?` deserializes cleanly. NaN is
        // surfaced by `validate()` below as a user-friendly error
        // instead of silently shipping bad data.
        const parsedPrice = trimmedPrice === "" ? null : Number(trimmedPrice);
        const payload: AssetUpsertPayload = {
            id: state.id,
            sageOfficeId: state.sageOfficeId,
            type: state.type,
            make: state.make.trim(),
            model: state.model.trim(),
            serialNumber: state.serialNumber.trim(),
            assetTag: trimmedTag === "" ? null : trimmedTag,
            purchaseDate: trimmedDate === "" ? null : trimmedDate,
            purchasePrice: parsedPrice,
            ...(inlineTarget !== null ? { assignTo: inlineTarget } : {}),
        };
        const error = validate(payload);
        if (error !== null) {
            setValidationError(error);
            return;
        }
        setValidationError(null);
        // Persist sticky target at submit time, not pick time — that way
        // bouncing through several picks while shopping doesn't pollute
        // "last used" until the user actually commits. Each kind has its
        // own bucket so a desk-walking campaign and a laptop-issuing
        // campaign don't clobber each other.
        if (showInlineAssignTo) {
            try {
                if (rememberAssignTo && inlineTarget !== null) {
                    if (inlineTarget.kind === "Workspace") {
                        sessionStorage.setItem(STICKY_ASSIGN_TO_WORKSPACE_KEY, inlineTarget.id);
                    } else {
                        sessionStorage.setItem(STICKY_ASSIGN_TO_USER_KEY, inlineTarget.id);
                    }
                } else if (!rememberAssignTo && inlineTarget !== null) {
                    if (inlineTarget.kind === "Workspace") {
                        sessionStorage.removeItem(STICKY_ASSIGN_TO_WORKSPACE_KEY);
                    } else {
                        sessionStorage.removeItem(STICKY_ASSIGN_TO_USER_KEY);
                    }
                }
            } catch {
                // sessionStorage can throw in private mode; sticky picker
                // is a UX nicety, not a correctness requirement.
            }
        }
        onSave(payload);
    }

    const isDeletedRecord = mode.kind === "edit" && mode.record.isDeleted;
    const isReadOnly = isDeletedRecord || !canEdit;
    const hasOpenAssignment =
        mode.kind === "edit" &&
        mode.record.currentAssignmentId !== null &&
        mode.record.currentAssignmentId !== undefined;

    const auditCreated = mode.kind === "edit" ? mode.record.created : null;
    const auditModified = mode.kind === "edit" ? mode.record.modified : null;
    const auditDeleted = mode.kind === "edit" ? mode.record.deleted : null;

    // Same shape as WorkspaceForm: include deleted offices on edit only so
    // a historical pick remains visible (labelled), but the create path
    // never offers a deleted target.
    const officesQuery = useSageOffices({ q: "", includeDeleted: mode.kind === "edit" });
    const offices = officesQuery.data ?? [];
    const officeOptions = offices.map((o) => ({
        value: o.id,
        label: o.isDeleted ? `${o.name} (deleted)` : o.name,
    }));
    const typeOptions = assetTypes.map((t) => ({ value: t, label: t }));

    // Extracted so the same actions can live either in the desktop
    // toolbar trailing slot or in the phone BottomActionBar without
    // duplicating the conditional rendering for delete/restore/photo.
    const actionButtons = (
        <>
            {canEdit && mode.kind === "create" ? (
                <Button
                    variant="secondary"
                    leadingIcon={<Camera size={16} />}
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isSaving || photoFillMutation.isPending}
                >
                    {photoFillMutation.isPending ? "Reading photo…" : "Fill from photo"}
                </Button>
            ) : null}
            <Button variant="ghost" onClick={onCancel} disabled={isSaving}>
                Cancel
            </Button>
            {canEdit && mode.kind === "edit" && isDeletedRecord ? (
                <Button
                    variant="secondary"
                    leadingIcon={<RotateCcw size={16} />}
                    onClick={() => onRequestRestore(mode.record.id)}
                    disabled={isSaving}
                >
                    Restore
                </Button>
            ) : null}
            {canEdit && mode.kind === "edit" && !isDeletedRecord ? (
                <Button
                    variant="danger"
                    leadingIcon={<Trash2 size={16} />}
                    onClick={() => onRequestDelete(mode.record.id)}
                    disabled={isSaving || hasOpenAssignment}
                >
                    Delete
                </Button>
            ) : null}
            {canEdit ? (
                <Button
                    variant="primary"
                    leadingIcon={<Save size={16} />}
                    onClick={handleSubmit}
                    disabled={isSaving || isDeletedRecord}
                >
                    {isSaving ? "Saving…" : "Save"}
                </Button>
            ) : null}
        </>
    );

    return (
        <Stack direction="column" gap={0} grow>
            <Toolbar
                leading={
                    <Stack direction="row" gap={2} align="center">
                        <Text role="heading">
                            {mode.kind === "create"
                                ? workspaceContext !== undefined
                                    ? `New asset for ${workspaceContext.name}`
                                    : "New asset"
                                : formatAssetIdentity(mode.record)}
                        </Text>
                        {isDeletedRecord ? (
                            <Badge tone="danger">Deleted</Badge>
                        ) : hasOpenAssignment ? (
                            <Badge tone="accent">Assigned</Badge>
                        ) : workspaceContext !== undefined && mode.kind === "create" ? (
                            <Badge tone="accent">Assigning to workspace</Badge>
                        ) : null}
                    </Stack>
                }
                trailing={
                    isPhone ? null : (
                        <Stack direction="row" gap={2} wrap>
                            {actionButtons}
                        </Stack>
                    )
                }
            />

            <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                onChange={handlePhotoSelected}
                style={{ display: "none" }}
                aria-hidden="true"
                tabIndex={-1}
            />

            <div style={{ flex: 1, overflow: "auto", padding: space[5] }}>
                <Stack gap={4} style={{ maxWidth: 720 }}>
                    {photoFillError !== null ? (
                        <Surface tone="raised" padding={3}>
                            <Text role="muted" color={palette.danger}>
                                Photo fill — {photoFillError}
                            </Text>
                        </Surface>
                    ) : null}
                    {validationError !== null ? (
                        <Surface tone="raised" padding={3}>
                            <Text role="muted" color={palette.danger}>
                                {validationError}
                            </Text>
                        </Surface>
                    ) : null}
                    {errorMessage !== null ? (
                        <Surface tone="raised" padding={3}>
                            <Text role="muted" color={palette.danger}>
                                {errorMessage}
                            </Text>
                        </Surface>
                    ) : null}

                    {matchProbeActive && existingMatch !== null ? (
                        <ExistingAssetMatchStrip
                            existing={existingMatch}
                            isProbing={isProbingMatch}
                            workspaceContext={workspaceContext}
                            canReassign={onReassignExisting !== undefined}
                            isWorking={isSaving}
                            onReassign={() => {
                                if (onReassignExisting !== undefined && existingMatch !== null) {
                                    onReassignExisting(existingMatch);
                                }
                            }}
                            onDismiss={() => setMatchDismissed(true)}
                        />
                    ) : null}

                    {canEdit && mode.kind === "edit" && !isDeletedRecord ? (
                        <AssignmentSection
                            asset={mode.record}
                            onAssignNew={onAssignNew}
                            onReassign={onReassign}
                            onReleaseAssignment={onReleaseAssignment}
                            isAssignmentBusy={isAssignmentBusy}
                            errorMessage={assignmentErrorMessage ?? null}
                        />
                    ) : null}

                    {canEdit && showInlineAssignTo ? (
                        <InlineAssignToSection
                            kind={inlineAssignKind}
                            onKindChange={(k) => {
                                setUserOverrodeKind(true);
                                setOverrideKind(k);
                            }}
                            workspaceValue={inlineAssignWorkspace}
                            onWorkspaceChange={setInlineAssignWorkspace}
                            userValue={inlineAssignUser}
                            onUserChange={setInlineAssignUser}
                            remember={rememberAssignTo}
                            onRememberChange={setRememberAssignTo}
                            disabled={isSaving}
                        />
                    ) : null}

                    <FormField label="Sage office" required>
                        {(id) => (
                            <Select
                                id={id}
                                value={state.sageOfficeId}
                                onChange={(e) =>
                                    setState((p) => ({ ...p, sageOfficeId: e.target.value }))
                                }
                                options={officeOptions}
                                placeholder={
                                    officesQuery.isPending ? "Loading offices…" : "Select an office"
                                }
                                    disabled={isReadOnly || officesQuery.isPending}
                            />
                        )}
                    </FormField>

                    <Stack direction={isPhone ? "column" : "row"} gap={3}>
                        <FieldWithAiBadge confidence={aiFilledFields.type}>
                            <FormField label="Type" required>
                                {(id) => (
                                    <Select
                                        id={id}
                                        value={state.type}
                                        onChange={(e) => updateField("type", e.target.value)}
                                        options={typeOptions}
                                        placeholder="Select a type"
                                        disabled={isReadOnly}
                                    />
                                )}
                            </FormField>
                        </FieldWithAiBadge>
                        <FieldWithAiBadge
                            confidence={aiFilledFields.assetTag}
                            scanFormat={scanFilledFields.assetTag}
                        >
                            <FormField label="Asset tag" hint="Optional. Unique when present (case-insensitive)">
                                {(id) => (
                                    <Stack direction="row" gap={2} align="center">
                                        <div style={{ flex: 1 }}>
                                            <TextInput
                                                id={id}
                                                value={state.assetTag}
                                                onChange={(e) => updateField("assetTag", e.target.value)}
                                                disabled={isReadOnly}
                                                placeholder="e.g. SF-LAP-0042"
                                            />
                                        </div>
                                        {/* Trailing scan affordance — renders nothing on
                                            browsers without BarcodeDetector, so the field
                                            stays usable everywhere. */}
                                        <ScanBarcodeButton
                                            disabled={isReadOnly}
                                            ariaLabel="Scan asset tag barcode"
                                            title="Scan asset tag"
                                            description="Point the camera at the asset tag barcode."
                                            onScan={(result) =>
                                                applyScanResult("assetTag", result.value, result.format)
                                            }
                                        />
                                    </Stack>
                                )}
                            </FormField>
                        </FieldWithAiBadge>
                    </Stack>

                    <Stack direction={isPhone ? "column" : "row"} gap={3}>
                        <FieldWithAiBadge confidence={aiFilledFields.make}>
                            <FormField label="Make" required>
                                {(id) => (
                                    <TextInput
                                        id={id}
                                        value={state.make}
                                        onChange={(e) => updateField("make", e.target.value)}
                                        disabled={isReadOnly}
                                    />
                                )}
                            </FormField>
                        </FieldWithAiBadge>
                        <FieldWithAiBadge confidence={aiFilledFields.model}>
                            <FormField label="Model" required>
                                {(id) => (
                                    <TextInput
                                        id={id}
                                        value={state.model}
                                        onChange={(e) => updateField("model", e.target.value)}
                                        disabled={isReadOnly}
                                    />
                                )}
                            </FormField>
                        </FieldWithAiBadge>
                    </Stack>

                    <FieldWithAiBadge
                        confidence={aiFilledFields.serialNumber}
                        scanFormat={scanFilledFields.serialNumber}
                        block
                    >
                        <FormField label="Serial number" required hint="Unique (case-insensitive)">
                            {(id) => (
                                <Stack direction="row" gap={2} align="center">
                                    <div style={{ flex: 1 }}>
                                        <TextInput
                                            id={id}
                                            value={state.serialNumber}
                                            onChange={(e) => updateField("serialNumber", e.target.value)}
                                            disabled={isReadOnly}
                                        />
                                    </div>
                                    {/* Same trailing scan affordance as the asset-tag row.
                                        Renders nothing on browsers without BarcodeDetector. */}
                                    <ScanBarcodeButton
                                        disabled={isReadOnly}
                                        ariaLabel="Scan serial number barcode"
                                        title="Scan serial number"
                                        description="Point the camera at the asset's serial number barcode."
                                        onScan={(result) =>
                                            applyScanResult("serialNumber", result.value, result.format)
                                        }
                                    />
                                </Stack>
                            )}
                        </FormField>
                    </FieldWithAiBadge>

                    <Stack direction={isPhone ? "column" : "row"} gap={3}>
                        <FormField label="Purchase date" hint="Optional. Used for depreciation and warranty tracking.">
                            {(id) => (
                                <TextInput
                                    id={id}
                                    type="date"
                                    value={state.purchaseDate}
                                    onChange={(e) => updateField("purchaseDate", e.target.value)}
                                    disabled={isReadOnly}
                                />
                            )}
                        </FormField>
                        <FormField label="Purchase price" hint="Optional. Amount paid at acquisition.">
                            {(id) => (
                                <TextInput
                                    id={id}
                                    type="number"
                                    inputMode="decimal"
                                    min={0}
                                    step="0.01"
                                    value={state.purchasePrice}
                                    onChange={(e) => updateField("purchasePrice", e.target.value)}
                                    disabled={isReadOnly}
                                    placeholder="e.g. 1299.99"
                                />
                            )}
                        </FormField>
                    </Stack>

                    {mode.kind === "edit" ? (
                        <Surface tone="raised" padding={4}>
                            <Stack gap={2}>
                                <Text role="eyebrow">Audit</Text>
                                <Text role="muted">Created — {formatActor(auditCreated)}</Text>
                                <Text role="muted">Modified — {formatActor(auditModified)}</Text>
                                {auditDeleted ? (
                                    <Text role="muted" color={palette.danger}>
                                        Deleted — {formatActor(auditDeleted)}
                                    </Text>
                                ) : null}
                                {/* The "Assignment" section above the audit panel
                                    surfaces the current target with friendly name,
                                    so we no longer echo the raw assignment id here. */}
                            </Stack>
                        </Surface>
                    ) : null}
                </Stack>
            </div>

            {isPhone ? <BottomActionBar>{actionButtons}</BottomActionBar> : null}
        </Stack>
    );
}

function initialState(mode: AssetFormMode): FormState {
    if (mode.kind === "create") {
        return {
            id: crypto.randomUUID(),
            sageOfficeId: "",
            type: "",
            make: "",
            model: "",
            serialNumber: "",
            assetTag: "",
            purchaseDate: "",
            purchasePrice: "",
        };
    }
    return {
        id: mode.record.id,
        // Legacy records persisted before sageOfficeId became required
        // arrive with a null here. The Select shows it as unselected
        // and `validate()` will force the user to pick one before save.
        sageOfficeId: mode.record.sageOfficeId ?? "",
        type: mode.record.type,
        make: mode.record.make,
        model: mode.record.model,
        serialNumber: mode.record.serialNumber,
        assetTag: mode.record.assetTag ?? "",
        // Wire shape for purchaseDate is the ISO "YYYY-MM-DD" string
        // that `<input type="date">` consumes directly; no parsing.
        // Price round-trips as a JS number so we stringify for the
        // text-typed numeric input, deferring locale-aware display
        // formatting to read-only views.
        purchaseDate: mode.record.purchaseDate ?? "",
        purchasePrice:
            mode.record.purchasePrice !== null && mode.record.purchasePrice !== undefined
                ? String(mode.record.purchasePrice)
                : "",
    };
}

function validate(payload: AssetUpsertPayload): string | null {
    if (payload.sageOfficeId === "") return "Pick the sage office this asset belongs to.";
    if (payload.type === "") return "Type is required.";
    if (payload.make === "") return "Make is required.";
    if (payload.model === "") return "Model is required.";
    if (payload.serialNumber === "") return "Serial number is required.";
    if (payload.purchasePrice !== null) {
        if (Number.isNaN(payload.purchasePrice)) {
            return "Purchase price must be a number.";
        }
        if (payload.purchasePrice < 0) {
            return "Purchase price cannot be negative.";
        }
    }
    return null;
}

// Wraps a FormField so a per-field "AI · 87%" badge renders just below
// it whenever the recognizer pre-filled that value. PR #5 will turn this
// into the interactive reassignment chip; for now it's purely informative.
function FieldWithAiBadge({
    confidence,
    scanFormat,
    block = false,
    children,
}: {
    readonly confidence: number | undefined;
    /** Detected barcode format (e.g. "code_128") if scan-filled. */
    readonly scanFormat?: string | undefined;
    readonly block?: boolean;
    readonly children: ReactNode;
}): ReactElement {
    const wrapperStyle = block ? undefined : { flex: 1 };
    // Scan beats AI for the same field — barcode reads are exact, OCR is
    // best-effort. The form lets both states coexist (one fill from photo,
    // one from scan), but the badge only shows the higher-trust source.
    return (
        <div style={wrapperStyle}>
            {children}
            {scanFormat !== undefined ? (
                <div style={{ marginTop: space[1] }}>
                    <Badge tone="ok">Scanned</Badge>
                </div>
            ) : confidence !== undefined ? (
                <div style={{ marginTop: space[1] }}>
                    <Badge tone={confidence < 0.5 ? "warn" : "neutral"}>
                        AI · {Math.round(confidence * 100)}%
                    </Badge>
                </div>
            ) : null}
        </div>
    );
}

interface InlineAssignToSectionProps {
    readonly kind: AssetAssignTarget["kind"];
    readonly onKindChange: (kind: AssetAssignTarget["kind"]) => void;
    readonly workspaceValue: WorkspaceView | null;
    readonly onWorkspaceChange: (workspace: WorkspaceView | null) => void;
    readonly userValue: UserDirectoryEntry | null;
    readonly onUserChange: (user: UserDirectoryEntry | null) => void;
    readonly remember: boolean;
    readonly onRememberChange: (remember: boolean) => void;
    readonly disabled: boolean;
}

/**
 * Optional collapsible section on the standalone Assets create form that
 * lets a user assign-to-workspace OR assign-to-user in the same round-
 * trip as the create. When set, the page-level save handler routes
 * through the matching atomic endpoint
 * (`add-asset-to-{workspace,user}`).
 *
 * Sticky behavior: each kind has its own sessionStorage bucket so a
 * desk-walking campaign and a laptop-issuing campaign don't clobber each
 * other. The kind itself is derived from the asset type (smart default)
 * unless the user has manually overridden via the toggle.
 */
function InlineAssignToSection({
    kind,
    onKindChange,
    workspaceValue,
    onWorkspaceChange,
    userValue,
    onUserChange,
    remember,
    onRememberChange,
    disabled,
}: InlineAssignToSectionProps): ReactElement {
    const hasPick = kind === "Workspace" ? workspaceValue !== null : userValue !== null;
    const rememberLabel =
        kind === "Workspace"
            ? "Remember this workspace for the next asset I create"
            : "Remember this user for the next asset I create";
    return (
        <Surface tone="raised" padding={3}>
            <Stack gap={2}>
                <Stack direction="row" align="center" gap={2}>
                    <Text role="eyebrow">Assign to (optional)</Text>
                    {hasPick ? <Badge tone="accent">Will assign on save</Badge> : null}
                </Stack>
                <TargetKindToggle value={kind} onChange={onKindChange} disabled={disabled} />
                {kind === "Workspace" ? (
                    <WorkspacePicker
                        value={workspaceValue}
                        onChange={onWorkspaceChange}
                        disabled={disabled}
                    />
                ) : (
                    <UserPicker
                        value={userValue}
                        onChange={onUserChange}
                        disabled={disabled}
                    />
                )}
                {hasPick ? (
                    <label
                        style={{
                            display: "flex",
                            alignItems: "center",
                            gap: space[2],
                            cursor: disabled ? "not-allowed" : "pointer",
                            color: palette.fgMuted,
                            fontSize: "0.85rem",
                        }}
                    >
                        <input
                            type="checkbox"
                            checked={remember}
                            disabled={disabled}
                            onChange={(e) => onRememberChange(e.target.checked)}
                        />
                        {rememberLabel}
                    </label>
                ) : null}
            </Stack>
        </Surface>
    );
}

interface ExistingAssetMatchStripProps {
    readonly existing: AssetView;
    readonly isProbing: boolean;
    readonly workspaceContext: AssetFormWorkspaceContext | undefined;
    readonly canReassign: boolean;
    readonly isWorking: boolean;
    readonly onReassign: () => void;
    readonly onDismiss: () => void;
}

/**
 * Inline strip that surfaces when the photo-fill recognizer's serial
 * number or asset tag matches an existing asset in inventory. Two flavors:
 *
 * - **Workspace-anchored** (`workspaceContext` set + `canReassign`):
 *   offers "Re-assign here" so a campaign user can re-place an asset
 *   they spotted at a new desk without rebuilding it from scratch.
 * - **Standalone create** (no workspace context): just informs the user
 *   that a duplicate exists. Saving from here will hit the uniqueness
 *   guard's 409, which is the right error for this branch.
 *
 * Either flavor can be dismissed with "Not the same" — the user is then
 * trusted to override (or to fix the recognizer's mistake).
 */
function ExistingAssetMatchStrip({
    existing,
    isProbing,
    workspaceContext,
    canReassign,
    isWorking,
    onReassign,
    onDismiss,
}: ExistingAssetMatchStripProps): ReactElement {
    const showReassign = workspaceContext !== undefined && canReassign;
    return (
        <Surface tone="raised" padding={3}>
            <Stack gap={2}>
                <Stack gap={1}>
                    <Text role="eyebrow">
                        {isProbing ? "Checking inventory…" : "Looks familiar"}
                    </Text>
                    <Text role="body" weight="semibold">
                        {formatAssetDisplay(existing, existing.id)}
                    </Text>
                    {showReassign ? (
                        <Stack direction="row" align="center" gap={1}>
                            <Text role="muted">
                                {existing.currentAssignmentId !== null &&
                                existing.currentAssignmentId !== undefined &&
                                existing.currentAssignmentId !== ""
                                    ? "Currently assigned elsewhere"
                                    : "Currently unassigned"}
                            </Text>
                            <ArrowRight size={14} style={{ opacity: 0.55 }} />
                            <Text role="muted">
                                {workspaceContext.name} · {workspaceContext.type}
                            </Text>
                        </Stack>
                    ) : (
                        <Text role="muted">
                            Saving will fail with a duplicate-serial error. If this is the
                            same asset, edit it from the Assets page instead.
                        </Text>
                    )}
                </Stack>
                <Stack direction="row" gap={2} justify="flex-end">
                    <Button variant="ghost" size="sm" onClick={onDismiss} disabled={isWorking}>
                        Not the same
                    </Button>
                    {showReassign ? (
                        <Button
                            variant="primary"
                            size="sm"
                            onClick={onReassign}
                            disabled={isWorking}
                        >
                            {isWorking ? "Re-assigning…" : "Re-assign here"}
                        </Button>
                    ) : null}
                </Stack>
            </Stack>
        </Surface>
    );
}

interface AssignmentSectionProps {
    readonly asset: AssetView;
    // Widened to `| undefined` (rather than `?`) so the parent can pass
    // its own optional callbacks through without conditional-spread
    // gymnastics. exactOptionalPropertyTypes treats `?` as "key may be
    // omitted" while `| undefined` is "value may be undefined".
    readonly onAssignNew: ((target: AssetAssignTarget) => void) | undefined;
    readonly onReassign: ((currentAssignmentId: string, target: AssetAssignTarget) => void) | undefined;
    readonly onReleaseAssignment: ((currentAssignmentId: string) => void) | undefined;
    readonly isAssignmentBusy: boolean;
    readonly errorMessage: string | null;
}

/**
 * Edit-mode-only section that lives just under the form header and lets
 * the user manage the asset's current assignment without leaving the
 * detail screen. Two states:
 *
 *   - **Available** (no open assignment): single "Assign to…" CTA opens
 *     the AssignTargetDialog defaulted to the asset's smart kind
 *     (Laptop → User, else Workspace).
 *   - **Assigned** (open assignment exists): shows the current target's
 *     friendly name, with "Re-assign to…" and "Release" actions. The
 *     dialog defaults to the same kind as the current assignment so the
 *     common case (workspace-to-workspace move) is one click.
 *
 * Action wiring is delegated upward via callbacks; the page owns the
 * mutation hooks and refetches. Section renders quietly (no buttons) if
 * the parent did not supply the corresponding callback — that's the
 * "this host doesn't surface assignment management here" signal used
 * by inventory mode.
 */
function AssignmentSection({
    asset,
    onAssignNew,
    onReassign,
    onReleaseAssignment,
    isAssignmentBusy,
    errorMessage,
}: AssignmentSectionProps): ReactElement | null {
    const canAssign = onAssignNew !== undefined;
    const canReassign = onReassign !== undefined;
    const canRelease = onReleaseAssignment !== undefined;

    // All hook calls must precede the early-return below — otherwise
    // unmounting a parent that toggles capability props mid-flight would
    // change hook order across renders.
    const [dialog, setDialog] = useState<
        | { kind: "closed" }
        | { kind: "assign" }
        | { kind: "reassign"; currentAssignmentId: string; currentTargetLabel: string }
    >({ kind: "closed" });

    // Resolve the current open assignment for this asset (if any). One row
    // at most; same point-search the Assignments page uses elsewhere.
    const assignmentsQuery = useAssetAssignments({
        assetId: asset.id,
        userId: "",
        workspaceId: "",
        targetKind: "",
        openOnly: true,
    });
    const currentAssignment = assignmentsQuery.data?.[0] ?? null;

    // For the "assigned" branch we need a friendly name for the current
    // target. Use the matching directory hook based on target kind.
    const userTargetId =
        currentAssignment?.assignedToUserId !== null &&
        currentAssignment?.assignedToUserId !== undefined &&
        currentAssignment.assignedToUserId !== ""
            ? currentAssignment.assignedToUserId
            : null;
    const workspaceTargetId =
        currentAssignment?.assignedToWorkspaceId !== null &&
        currentAssignment?.assignedToWorkspaceId !== undefined &&
        currentAssignment.assignedToWorkspaceId !== ""
            ? currentAssignment.assignedToWorkspaceId
            : null;
    const userEntry = useUserDirectoryEntry(userTargetId);
    const workspaceEntry = useWorkspaceEntry(workspaceTargetId);

    const currentTargetLabel = useMemo(() => {
        if (userTargetId !== null) {
            return formatUserDisplay(userEntry.data ?? null, userTargetId);
        }
        if (workspaceTargetId !== null) {
            const ws = workspaceEntry.data ?? null;
            if (ws !== null) return `${ws.name} · ${ws.type}`;
            return workspaceTargetId;
        }
        return "—";
    }, [userTargetId, userEntry.data, workspaceTargetId, workspaceEntry.data]);

    if (!canAssign && !canReassign && !canRelease) {
        // Nothing the user can do here — render nothing rather than a
        // useless read-only block. Safe to return early here: every hook
        // above runs unconditionally on every render.
        return null;
    }

    const currentKind: AssetAssignTarget["kind"] | null =
        userTargetId !== null
            ? "User"
            : workspaceTargetId !== null
              ? "Workspace"
              : null;

    // Smart default for the dialog when assigning a previously-unassigned
    // asset: derive from asset type. When re-assigning, default to the
    // SAME kind as the current assignment (principle of least surprise —
    // users moving a desk asset are almost always picking another desk).
    const defaultKindForAssign = smartDefaultKindForType(asset.type);
    const defaultKindForReassign: AssetAssignTarget["kind"] =
        currentKind ?? defaultKindForAssign;

    function handleSubmit(target: AssetAssignTarget): void {
        if (dialog.kind === "assign" && onAssignNew !== undefined) {
            onAssignNew(target);
            setDialog({ kind: "closed" });
            return;
        }
        if (dialog.kind === "reassign" && onReassign !== undefined) {
            onReassign(dialog.currentAssignmentId, target);
            setDialog({ kind: "closed" });
            return;
        }
    }

    const dialogProps =
        dialog.kind === "assign"
            ? {
                  open: true,
                  mode: { kind: "assign" } as AssignTargetDialogMode,
                  defaultKind: defaultKindForAssign,
              }
            : dialog.kind === "reassign"
              ? {
                    open: true,
                    mode: {
                        kind: "reassign" as const,
                        currentTargetLabel: dialog.currentTargetLabel,
                    } as AssignTargetDialogMode,
                    defaultKind: defaultKindForReassign,
                }
              : null;

    return (
        <Surface tone="raised" padding={3}>
            <Stack gap={2}>
                <Stack direction="row" align="center" justify="space-between" gap={2} wrap>
                    <Stack gap={0}>
                        <Text role="eyebrow">Assignment</Text>
                        {assignmentsQuery.isPending ? (
                            <Text role="muted">Loading…</Text>
                        ) : currentAssignment === null ? (
                            <Text role="body">Not currently assigned</Text>
                        ) : (
                            <Text role="body">Assigned to {currentTargetLabel}</Text>
                        )}
                    </Stack>
                    <Stack direction="row" gap={2} wrap>
                        {currentAssignment === null ? (
                            canAssign ? (
                                <Button
                                    variant="primary"
                                    size="sm"
                                    onClick={() => setDialog({ kind: "assign" })}
                                    disabled={isAssignmentBusy}
                                >
                                    Assign to…
                                </Button>
                            ) : null
                        ) : (
                            <>
                                {canReassign ? (
                                    <Button
                                        variant="primary"
                                        size="sm"
                                        onClick={() =>
                                            setDialog({
                                                kind: "reassign",
                                                currentAssignmentId: currentAssignment.id,
                                                currentTargetLabel,
                                            })
                                        }
                                        disabled={isAssignmentBusy}
                                    >
                                        Re-assign to…
                                    </Button>
                                ) : null}
                                {canRelease ? (
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        leadingIcon={<Unlink size={14} />}
                                        onClick={() => onReleaseAssignment(currentAssignment.id)}
                                        disabled={isAssignmentBusy}
                                    >
                                        Release
                                    </Button>
                                ) : null}
                            </>
                        )}
                    </Stack>
                </Stack>
                {errorMessage !== null ? (
                    <Text role="muted" color={palette.danger}>
                        {errorMessage}
                    </Text>
                ) : null}
            </Stack>

            {dialogProps !== null ? (
                <AssignTargetDialog
                    open={dialogProps.open}
                    mode={dialogProps.mode}
                    defaultKind={dialogProps.defaultKind}
                    assetLabel={formatAssetDisplay(asset, asset.id)}
                    onSubmit={handleSubmit}
                    onCancel={() => setDialog({ kind: "closed" })}
                    isWorking={isAssignmentBusy}
                />
            ) : null}
        </Surface>
    );
}
